import React, { useState, useEffect } from "react";
import MainCalculatorsList from "../FoodCalculator/MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";
import imgUrl from "../../../images/pizza.webp";

const TITLE = "Kalkulator Pizza - Hitung Jumlah Adonan dan Bahan untuk Ukuran Apapun";
const DESCRIPTION =
  "Kalkulator pizza gratis - hitung jumlah adonan, saus, dan keju untuk berapa pun jumlah pizza. Tentukan ukuran dan jumlah pizza lalu dapatkan resep yang tepat seketika.";
const KEYWORDS =
  "kalkulator pizza, jumlah adonan pizza, resep pizza, hitung bahan pizza, jumlah keju di pizza, pizza calculator, pizza dough calculator";
const OG_URL = "https://do-calculate.com/calculator/id/pizza/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Kalkulator Pizza",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Beranda", item: "https://do-calculate.com/" },
      { "@type": "ListItem", position: 2, name: "Kalkulator", item: "https://do-calculate.com/id/all-calculators/" },
      { "@type": "ListItem", position: 3, name: "Kalkulator Pizza", item: OG_URL },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Berapa jumlah adonan yang dibutuhkan untuk pizza diameter 30 cm?",
        acceptedAnswer: { "@type": "Answer", text: "Untuk pizza diameter 30 cm (ukuran sedang), Anda membutuhkan sekitar 250 gram adonan. Jumlah ini tergantung pada ketebalan adonan yang Anda inginkan." },
      },
      {
        "@type": "Question",
        name: "Berapa jumlah saus yang tepat untuk pizza?",
        acceptedAnswer: { "@type": "Answer", text: "Jumlah saus yang tepat untuk pizza ukuran sedang (30 cm) adalah sekitar 80-100 gram. Rata-rata umum adalah 35-40 gram per 100 cm² luas pizza." },
      },
      {
        "@type": "Question",
        name: "Apa perbedaan antara adonan tipis dan tebal?",
        acceptedAnswer: { "@type": "Answer", text: "Adonan pizza tipis (gaya Italia) menggunakan sekitar 200-220 gram untuk pizza 30 cm, sedangkan adonan tebal (gaya Amerika) membutuhkan 280-320 gram untuk diameter yang sama." },
      },
    ],
  },
];

const PIZZA_SIZES = {
  small: { name: "Kecil", diameter: 20 },
  medium: { name: "Sedang", diameter: 25 },
  large: { name: "Besar", diameter: 30 },
  xlarge: { name: "Sangat Besar", diameter: 35 },
  custom: { name: "Kustom", diameter: 0 },
};

const DOUGH_STYLES = {
  thin: { name: "Adonan Tipis (Italia)", factor: 0.85 },
  regular: { name: "Adonan Biasa (Klasik)", factor: 1.0 },
  thick: { name: "Adonan Tebal (Amerika)", factor: 1.3 },
  deepdish: { name: "Adonan Dalam (Deep Dish)", factor: 1.6 },
};

const BASE_DOUGH_PER_CM2 = 0.35;

function PizzaCalculator() {
  const [sizePreset, setSizePreset] = useState("large");
  const [customDiameter, setCustomDiameter] = useState("");
  const [doughStyle, setDoughStyle] = useState("regular");
  const [numberOfPizzas, setNumberOfPizzas] = useState(1);
  const [hydration, setHydration] = useState(65);
  const [saltPercent, setSaltPercent] = useState(2);
  const [yeastPercent, setYeastPercent] = useState(0.3);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const getDiameter = () => {
    if (sizePreset === "custom") return parseFloat(customDiameter || 0);
    return PIZZA_SIZES[sizePreset].diameter;
  };

  const calculatePizza = () => {
    try {
      setError("");
      const diameter = getDiameter();
      if (!diameter || diameter <= 0) { setError("Masukkan diameter pizza yang valid"); setResult(null); return; }
      if (numberOfPizzas < 1) { setError("Masukkan jumlah pizza yang valid"); setResult(null); return; }

      const styleFactor = DOUGH_STYLES[doughStyle].factor;
      const radius = diameter / 2;
      const areaCm2 = Math.PI * radius * radius;
      const doughWeightOnePizza = areaCm2 * BASE_DOUGH_PER_CM2 * styleFactor;
      const totalDoughWeight = doughWeightOnePizza * numberOfPizzas;

      const hydrationFrac = hydration / 100;
      const saltFrac = saltPercent / 100;
      const yeastFrac = yeastPercent / 100;
      const divisor = 1 + hydrationFrac + saltFrac + yeastFrac;

      const flourTotal = totalDoughWeight / divisor;
      const waterTotal = flourTotal * hydrationFrac;
      const saltTotal = flourTotal * saltFrac;
      const yeastTotal = flourTotal * yeastFrac;
      const saucePerPizza = areaCm2 * 0.35;
      const totalSauce = saucePerPizza * numberOfPizzas;
      const cheesePerPizza = areaCm2 * 0.5;
      const totalCheese = cheesePerPizza * numberOfPizzas;
      const totalSlices = numberOfPizzas * 8;

      setResult({
        diameter, areaCm2, doughWeightOnePizza, totalDoughWeight,
        flourTotal, waterTotal, saltTotal, yeastTotal,
        saucePerPizza, totalSauce, cheesePerPizza, totalCheese, totalSlices,
        styleName: DOUGH_STYLES[doughStyle].name,
        sizeName: sizePreset === "custom" ? `Kustom (${diameter} cm)` : `${PIZZA_SIZES[sizePreset].name} (${diameter} cm)`,
      });
    } catch (err) {
      setError("Kesalahan perhitungan: " + err.message);
      setResult(null);
    }
  };

  useEffect(() => { calculatePizza(); }, [sizePreset, customDiameter, doughStyle, numberOfPizzas, hydration, saltPercent, yeastPercent]);

  const handleClear = () => {
    setSizePreset("large"); setCustomDiameter(""); setDoughStyle("regular");
    setNumberOfPizzas(1); setHydration(65); setSaltPercent(2); setYeastPercent(0.3);
    setResult(null); setError("");
  };

  return (
    <div className="ltr" style={{ direction: "ltr" }}>
      <SharedHelmet TITLE={TITLE} DESCRIPTION={DESCRIPTION} KEYWORDS={KEYWORDS} OG_URL={OG_URL} SCHEMA={SCHEMA} IMAGE={imgUrl} />
      <div className="row">
        <div className="col-12 col-md-8"><FirstAds /></div>
        <div className="col-12 col-md-4 text-center">
          <img src={imgUrl} alt="Kalkulator Pizza - mengetahui jumlah bahan dengan tepat" width="380px" height="300px" />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center" style={{ padding: "15px", borderRadius: "5px" }}>
            <h1 style={{ fontSize: "22px", margin: 0 }}>Kalkulator Pizza 🍕</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>Kalkulator Pizza</strong> adalah alat gratis dan akurat yang membantu Anda <strong>menghitung jumlah adonan dan bahan</strong> untuk berapa pun jumlah pizza dengan ukuran yang Anda inginkan. Masukkan diameter pizza, jumlah, dan jenis adonan untuk mendapatkan resep yang tepat seketika. Sempurna untuk restoran, bakeri, memasak di rumah, dan persiapan pesta.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>Hitung Bahan Pizza dengan Mudah</h2>
              </div>

              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="sizePreset">Ukuran Pizza</label>
                  <select id="sizePreset" className="form-select" value={sizePreset} onChange={(e) => setSizePreset(e.target.value)}>
                    {Object.entries(PIZZA_SIZES).map(([key, { name, diameter }]) => (
                      <option key={key} value={key}>{name}{key !== "custom" ? ` — diameter ${diameter} cm` : ""}</option>
                    ))}
                  </select>
                </div>
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="doughStyle">Jenis Adonan</label>
                  <select id="doughStyle" className="form-select" value={doughStyle} onChange={(e) => setDoughStyle(e.target.value)}>
                    {Object.entries(DOUGH_STYLES).map(([key, { name }]) => (
                      <option key={key} value={key}>{name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {sizePreset === "custom" && (
                <div className="row">
                  <div className="col-sm-12">
                    <label htmlFor="customDiameter">Diameter Pizza Kustom (cm)</label>
                    <input id="customDiameter" type="number" className="form-control" value={customDiameter}
                      onChange={(e) => setCustomDiameter(e.target.value)} min="10" max="100" step="0.5" placeholder="Contoh: 28"
                      style={{ backgroundColor: "#fff3cd" }} />
                  </div>
                </div>
              )}

              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="numberOfPizzas">Jumlah Pizza</label>
                  <input id="numberOfPizzas" type="number" className="form-control" value={numberOfPizzas}
                    onChange={(e) => setNumberOfPizzas(parseInt(e.target.value) || 1)} min="1" max="100" placeholder="Jumlah pizza" />
                </div>
              </div>

              <div className="row" style={{ marginTop: "10px" }}>
                <div className="col-sm-12 col-md-4">
                  <label>Persentase Air (%) — Hidrasi</label>
                  <input type="number" className="form-control" value={hydration}
                    onChange={(e) => setHydration(parseFloat(e.target.value) || 65)} min="50" max="90" step="1" placeholder="65" />
                </div>
                <div className="col-sm-12 col-md-4">
                  <label>Persentase Garam (%)</label>
                  <input type="number" className="form-control" value={saltPercent}
                    onChange={(e) => setSaltPercent(parseFloat(e.target.value) || 2)} min="0" max="5" step="0.1" placeholder="2" />
                </div>
                <div className="col-sm-12 col-md-4">
                  <label>Persentase Ragi (%)</label>
                  <input type="number" className="form-control" value={yeastPercent}
                    onChange={(e) => setYeastPercent(parseFloat(e.target.value) || 0.3)} min="0.1" max="3" step="0.1" placeholder="0.3" />
                </div>
              </div>

              <div className="text-center mb-3 mt-4">
                <button onClick={handleClear} className="btn btn-secondary" style={{ fontSize: "16px", padding: "10px 30px" }}>Hapus</button>
              </div>

              {error && <div className="alert alert-warning" role="alert"><strong>Peringatan:</strong> {error}</div>}

              {result && (
                <div className="mt-4">
                  <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}>
                    <strong>Hasil:</strong>
                  </h3>
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>Keterangan</th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>Nilai</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr><td style={{ textAlign: "center" }}>Berat adonan per pizza</td><td style={{ textAlign: "center", fontSize: "18px", fontWeight: "bold", color: "#1a5f3f" }}>{result.doughWeightOnePizza.toFixed(0)} g</td></tr>
                        <tr><td style={{ textAlign: "center" }}>Total adonan ({numberOfPizzas} pizza)</td><td style={{ textAlign: "center", fontSize: "18px", fontWeight: "bold", color: "#0f3d28" }}>{result.totalDoughWeight.toFixed(0)} g</td></tr>
                        <tr><td style={{ textAlign: "center" }}>Tepung terigu</td><td style={{ textAlign: "center", fontSize: "16px" }}>{result.flourTotal.toFixed(0)} g</td></tr>
                        <tr><td style={{ textAlign: "center" }}>Air</td><td style={{ textAlign: "center", fontSize: "16px" }}>{result.waterTotal.toFixed(0)} ml</td></tr>
                        <tr><td style={{ textAlign: "center" }}>Garam</td><td style={{ textAlign: "center", fontSize: "16px" }}>{result.saltTotal.toFixed(1)} g</td></tr>
                        <tr><td style={{ textAlign: "center" }}>Ragi</td><td style={{ textAlign: "center", fontSize: "16px" }}>{result.yeastTotal.toFixed(1)} g</td></tr>
                        <tr><td style={{ textAlign: "center" }}>Saus (per pizza)</td><td style={{ textAlign: "center", fontSize: "16px" }}>{result.saucePerPizza.toFixed(0)} g</td></tr>
                        <tr><td style={{ textAlign: "center" }}>Keju Mozzarella (per pizza)</td><td style={{ textAlign: "center", fontSize: "16px" }}>{result.cheesePerPizza.toFixed(0)} g</td></tr>
                        <tr><td style={{ textAlign: "center" }}>Total potongan (8 per pizza)</td><td style={{ textAlign: "center", fontSize: "16px" }}>{result.totalSlices} potong</td></tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="info-sections-container">
                    <div className="info-section">
                      <div className="info-section-header"><strong>Ringkasan Resep</strong></div>
                      <div className="info-section-body">
                        <ul style={{ listStyle: "none", padding: 0, lineHeight: "2" }}>
                          <li>🍕 <strong>Ukuran:</strong> {result.sizeName}</li>
                          <li>🍞 <strong>Jenis Adonan:</strong> {result.styleName}</li>
                          <li>📐 <strong>Luas Pizza:</strong> {result.areaCm2.toFixed(0)} cm²</li>
                          <li>🧂 <strong>Total Saus ({numberOfPizzas} pizza):</strong> {result.totalSauce.toFixed(0)} g</li>
                          <li>🧀 <strong>Total Keju ({numberOfPizzas} pizza):</strong> {result.totalCheese.toFixed(0)} g</li>
                        </ul>
                        <div className="alert alert-info mt-3" style={{ fontSize: "14px" }}>
                          <strong>💡 Info:</strong> Hidrasi yang digunakan: {hydration}% — Hidrasi ideal untuk pizza rumahan antara 60-70%.
                        </div>
                      </div>
                    </div>
                    <div className="info-section">
                      <div className="info-section-header"><strong>Detail Perhitungan</strong></div>
                      <div className="info-section-body">
                        <div style={{ lineHeight: "1.8" }}>
                          <p><strong>Diameter:</strong> {result.diameter} cm</p>
                          <p><strong>Luas:</strong> {result.areaCm2.toFixed(2)} cm²</p>
                          <p><strong>Jenis Adonan:</strong> {result.styleName}</p>
                          <hr />
                          <p style={{ fontSize: "14px", color: "#666" }}><strong>Metode Perhitungan:</strong></p>
                          <p style={{ fontSize: "14px", color: "#666" }}>
                            Luas = π × (diameter/2)² = {result.areaCm2.toFixed(2)} cm²<br />
                            Berat adonan = Luas × 0.35 g/cm² × faktor jenis<br />
                            = {result.areaCm2.toFixed(2)} × 0.35 × {DOUGH_STYLES[doughStyle].factor}<br />
                            = {result.doughWeightOnePizza.toFixed(0)} g per pizza
                          </p>
                          <p style={{ fontSize: "14px", color: "#666" }}>
                            Tepung = Adonan ÷ (1 + hidrasi + garam + ragi)<br />
                            = {result.totalDoughWeight.toFixed(0)} ÷ (1 + {hydration / 100} + {saltPercent / 100} + {yeastPercent / 100})<br />
                            = {result.flourTotal.toFixed(0)} g tepung
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <hr />
            <div className="row"><RelatedLinks /></div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds /><br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header"><strong>Apa itu Kalkulator Pizza?</strong></div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Kalkulator Pizza</strong> adalah alat elektronik gratis yang menghitung jumlah semua bahan adonan pizza dengan tepat. Alih-alih menebak atau menggunakan resep perkiraan, kalkulator menggunakan persamaan pembuat roti profesional (Baker's Percentages) untuk memberikan resep yang benar-benar presisi. Anda dapat menyesuaikan ukuran pizza, jumlahnya, jenis adonan, dan proporsi bahan, lalu mendapatkan hasilnya seketika.
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>Cara Menggunakan Kalkulator:</h3>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Pilih ukuran pizza dari daftar atau masukkan diameter kustom</li>
                  <li>Tentukan jenis adonan (tipis, biasa, tebal, atau dalam)</li>
                  <li>Masukkan jumlah pizza yang ingin Anda buat</li>
                  <li>Sesuaikan persentase air, garam, dan ragi sesuai resep yang Anda sukai</li>
                  <li>Hasilnya akan muncul otomatis dan mencakup semua bahan dalam gram</li>
                </ol>
                <div className="alert alert-info mt-3" style={{ lineHeight: "1.8" }}>
                  <strong>💡 Catatan:</strong> Kalkulator menggunakan sistem Baker's Percentages di mana berat tepung selalu dianggap 100% dan setiap bahan dihitung sebagai persentasenya.
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>Jenis Adonan Pizza dan Tingkat Hidrasi</strong></div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>Hidrasi (persentase air terhadap tepung) adalah rahasia di balik tekstur adonan. Semakin tinggi persentasenya, semakin elastis adonan dan semakin sulit dibentuk. Berikut panduan singkatnya:</p>
                <table className="table bmi-table" style={{ marginTop: "1rem" }}>
                  <thead>
                    <tr>
                      <th className="bmi-header" style={{ textAlign: "center" }}>Jenis Adonan</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>Tingkat Hidrasi</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>Tekstur</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td style={{ textAlign: "center" }}>Italia Klasik (Neapolitan)</td><td style={{ textAlign: "center" }}>60-65%</td><td style={{ textAlign: "center" }}>Renyah, tipis, sedikit garing</td></tr>
                    <tr><td style={{ textAlign: "center" }}>New York</td><td style={{ textAlign: "center" }}>63-67%</td><td style={{ textAlign: "center" }}>Elastis, bisa dilipat, enak dingin</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Amerika Tebal</td><td style={{ textAlign: "center" }}>55-60%</td><td style={{ textAlign: "center" }}>Tebal, spons, lembut</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Deep Dish Chicago</td><td style={{ textAlign: "center" }}>50-55%</td><td style={{ textAlign: "center" }}>Padat, berlapis, dipanggang dalam loyang</td></tr>
                    <tr><td style={{ textAlign: "center" }}>Focaccia</td><td style={{ textAlign: "center" }}>75-80%</td><td style={{ textAlign: "center" }}>Sangat spons, gelembung besar</td></tr>
                  </tbody>
                </table>
                <div className="alert alert-success mt-3" style={{ lineHeight: "1.8" }}>
                  <strong>✓ Tips Ahli:</strong> Untuk pemula, mulailah dengan hidrasi 65%. Adonan akan seimbang, mudah dibentuk, dan memberikan hasil yang sangat baik di oven rumah.
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>Contoh Praktis</strong></div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>Contoh 1: Pizza Rumahan Satu Loyang (diameter 30 cm, adonan biasa)</h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Total berat adonan: 250 g</li>
                  <li>Tepung: 148 g — Air: 96 ml — Garam: 3 g — Ragi: 0.4 g</li>
                  <li>Saus: 83 g — Keju mozzarella: 118 g</li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>Contoh 2: Pesta 5 Pizza (diameter 35 cm, adonan tipis)</h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Berat adonan per pizza: 289 g — Total: 1446 g</li>
                  <li>Tepung: 854 g — Air: 555 ml — Garam: 17 g — Ragi: 2.6 g</li>
                  <li>Total saus: 577 g — Total keju: 962 g</li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>Contoh 3: Restoran — 20 Pizza Sehari (diameter 30 cm, adonan biasa)</h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Total adonan: 5008 g (sekitar 5 kg)</li>
                  <li>Tepung: 2964 g — Air: 1927 ml — Garam: 59 g — Ragi: 9 g</li>
                  <li>Saus: 3335 g — Keju: 4718 g</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>Pertanyaan yang Sering Diajukan — Kalkulator Pizza</strong></div>
              <div className="info-section-body">
                <div className="info-sections-container">
                  <div className="info-section">
                    <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Berapa jumlah pizza yang cukup untuk 10 orang?</h3></div>
                    <div className="info-section-body">
                      <p style={{ lineHeight: "1.8" }}>Untuk 10 orang dalam makan utama, disarankan 3-4 pizza ukuran besar (30 cm). Setiap pizza dipotong menjadi 8 bagian, sehingga totalnya 24-32 potong — yaitu 2-3 potong per orang. Jika pesta untuk anak-anak atau pizza hanya sebagai camilan, 2 pizza besar cukup untuk 10 orang.</p>
                    </div>
                  </div>
                  <div className="info-section">
                    <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Bisakah adonan pizza disimpan?</h3></div>
                    <div className="info-section-body">
                      <p style={{ lineHeight: "1.8" }}>Ya, adonan yang sudah dibuat bisa disimpan di kulkas selama 3-5 hari dalam wadah tertutup yang diolesi sedikit minyak zaitun. Penyimpanan dingin meningkatkan rasa secara signifikan — banyak restoran Italia yang memfermentasi adonannya selama 48 jam atau lebih.</p>
                    </div>
                  </div>
                  <div className="info-section">
                    <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Berapa jumlah keju yang tepat untuk pizza?</h3></div>
                    <div className="info-section-body">
                      <p style={{ lineHeight: "1.8" }}>Jumlah ideal keju mozzarella untuk pizza 30 cm adalah 120-150 gram. Kurang dari itu, pizza akan kering; lebih dari itu, dasarnya akan basah. Jika menggunakan ricotta atau cheddar bersama mozzarella, kurangi jumlah masing-masing namun tetap pertahankan total berat yang sama.</p>
                    </div>
                  </div>
                  <div className="info-section">
                    <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Apakah kalkulator ini cocok untuk penggunaan komersial di restoran?</h3></div>
                    <div className="info-section-body">
                      <p style={{ lineHeight: "1.8" }}>Tentu saja! Kalkulator mendukung perhitungan hingga ratusan pizza sekaligus, menjadikannya ideal untuk restoran dan bakeri. Proporsi yang digunakan sama seperti yang digunakan pembuat roti profesional di Italia dan Amerika. Anda dapat menyesuaikan hidrasi, garam, dan ragi untuk mencocokkan resep Anda sendiri.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PizzaCalculator;